import { useCallback, useRef } from "react";

import { useTranslation } from "react-i18next";

import { TFunction } from "i18next";

import { trackGoogleAnalyticsEvent } from "shared/hooks/useGoogleAnalytics/functions";
import { LANGUAGES } from "shared/types/enums";

const getEnglishNewsTitle = (t: TFunction, newsId: number) =>
    t(`Dashboard##news##title##${newsId}`, { lng: LANGUAGES.English });

const useNewsTracking = (newsId: number) => {
    const { t } = useTranslation();

    const openedAtRef = useRef<number | null>(null);
    const completeSentRef = useRef(false);

    const trackOpen = useCallback(() => {
        openedAtRef.current = Date.now();
        completeSentRef.current = false;

        trackGoogleAnalyticsEvent("news_article_open", {
            news_id: newsId,
            news_title: getEnglishNewsTitle(t, newsId)
        });
    }, [newsId, t]);

    const trackClose = useCallback(() => {
        if (openedAtRef.current === null) return;

        const readDurationSeconds = Math.round(
            (Date.now() - openedAtRef.current) / 1000
        );

        openedAtRef.current = null;

        trackGoogleAnalyticsEvent("news_article_close", {
            news_id: newsId,
            news_title: getEnglishNewsTitle(t, newsId),
            read_duration_seconds: readDurationSeconds
        });
    }, [newsId, t]);

    const trackScroll = useCallback(
        (element: HTMLElement) => {
            if (completeSentRef.current) return;

            const isAtBottom =
                element.scrollTop + element.clientHeight >=
                element.scrollHeight - 26; // small tolerance for rounding

            if (!isAtBottom) return;

            completeSentRef.current = true;

            trackGoogleAnalyticsEvent("news_article_full_read_complete", {
                news_id: newsId,
                news_title: getEnglishNewsTitle(t, newsId)
            });
        },
        [newsId, t]
    );

    return { trackOpen, trackClose, trackScroll };
};

export { useNewsTracking };
